import { useEffect, useRef, useState } from "react"
import { ConfigProvider, DatePicker } from "antd"
import { Dayjs } from "dayjs"

import styles from "./index.module.scss"

const PICKER_WIDTH = 288
const PADDING = 55

interface SingleDatePickerProps {
  value: Dayjs | null
  onSelect: (date: Dayjs | null) => void
}

export const SingleDatePicker = ({ value, onSelect }: SingleDatePickerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [offsetLeft, setOffsetLeft] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.scrollMargin = "10px"
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" })
    }
  }, [containerRef.current])

  useEffect(() => {
    const checkPosition = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth

      if (rect.left + PICKER_WIDTH > viewportWidth - PADDING) {
        setOffsetLeft(viewportWidth - PADDING - PICKER_WIDTH - rect.left)
      } else {
        setOffsetLeft(undefined)
      }
    }

    checkPosition()
    window.addEventListener("resize", checkPosition)
    return () => window.removeEventListener("resize", checkPosition)
  }, [])

  return (
    <div className={styles.pickerWrapper}>
      <div
        ref={containerRef}
        className={styles.pickerContainer}
        style={offsetLeft !== undefined ? { left: offsetLeft } : undefined}
      >
        <ConfigProvider
          theme={{
            components: {
              DatePicker: {
                cellHeight: 32,
              },
            },
            token: {
              motion: false,
              colorPrimary: "#43e0f8",
              colorPrimaryBg: "#ebfcff",
            },
          }}
        >
          <DatePicker
            open
            value={value}
            format="L"
            placement="topLeft"
            getPopupContainer={() => containerRef.current!}
            onChange={onSelect}
            size="middle"
            showToday={false}
          />
        </ConfigProvider>
      </div>
    </div>
  )
}
